import { useEffect, useRef, useState } from "react";
import { axiosRestInstance } from "../../../../../../../common/axios";
import {
  BenefitActivityLogItemType,
  BenefitActivityLogResponseType,
  BenefitActivityLogUserType,
} from "../../../../../../../redux/slices/partner-program/benefits-log/Types";
import { BenefitActivityDashboardResponseType } from "../../../../../../../redux/slices/partner-program/benefit-activity-dashboard/Types";
import { mapBenefitActivityDashboardResponse } from "../../../../../../../redux/slices/partner-program/benefit-activity-dashboard/mapper";

export type FilterOption = { label: string; value: string };

export type BenefitLogFilterOptions = {
  partners: FilterOption[];
  benefits: FilterOption[];
  statuses: FilterOption[];
  hidOwners: FilterOption[];
  partnerOwners: FilterOption[];
};

export const EMPTY_BENEFIT_LOG_FILTER_OPTIONS: BenefitLogFilterOptions = {
  partners: [],
  benefits: [],
  statuses: [],
  hidOwners: [],
  partnerOwners: [],
};

/**
 * - `dashboard`: all partners' logs (Benefit Activity Log tab).
 * - `partner`: one partner's logs (Benefits Log history).
 */
export type BenefitLogFilterOptionsSource =
  | { type: "dashboard" }
  | { type: "partner"; partnerId: number | null | undefined };

/** Both APIs paginate in memory after loading every matching row, so one large page returns all logs. */
const ALL_LOGS_LIMIT = 10000;

const getUserName = (user: BenefitActivityLogUserType) =>
  user.full_name?.trim() ||
  `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
  String(user.ID);

const toSortedOptions = (byValue: Map<string, string>): FilterOption[] =>
  Array.from(byValue, ([value, label]) => ({ label, value })).sort((a, b) =>
    a.label.localeCompare(b.label)
  );

/** Dropdown options containing only values that appear in the given logs. */
export const buildBenefitLogFilterOptions = (
  logs: BenefitActivityLogItemType[],
  { showPartnerOnOwner = false }: { showPartnerOnOwner?: boolean } = {}
): BenefitLogFilterOptions => {
  const partners = new Map<string, string>();
  const benefits = new Map<string, string>();
  const statuses = new Map<string, string>();
  const hidOwners = new Map<string, string>();
  const partnerOwners = new Map<string, string>();

  logs.forEach((log) => {
    if (log.partner_id) {
      partners.set(String(log.partner_id), log.partner_name || String(log.partner_id));
    }
    if (log.benefit_id) {
      benefits.set(String(log.benefit_id), log.benefits_name || String(log.benefit_id));
    }
    const status = (log.status || "").trim();
    if (status) statuses.set(status, status);
    (log.HID_user_id || []).forEach((user) => {
      if (user?.ID) hidOwners.set(String(user.ID), getUserName(user));
    });
    (log.partner_user_id || []).forEach((user) => {
      if (!user?.ID) return;
      const name = getUserName(user);
      partnerOwners.set(
        String(user.ID),
        showPartnerOnOwner && log.partner_name ? `${name} (${log.partner_name})` : name
      );
    });
  });

  return {
    partners: toSortedOptions(partners),
    benefits: toSortedOptions(benefits),
    statuses: toSortedOptions(statuses),
    hidOwners: toSortedOptions(hidOwners),
    partnerOwners: toSortedOptions(partnerOwners),
  };
};

const fetchAllLogs = async (
  source: BenefitLogFilterOptionsSource
): Promise<BenefitActivityLogItemType[]> => {
  if (source.type === "dashboard") {
    const query = { has_benefits: true, page: 1, limit: ALL_LOGS_LIMIT };
    const response = await axiosRestInstance.get<BenefitActivityDashboardResponseType>(
      "/partner-program/benefit-activity-log/dashboard",
      { params: query }
    );
    return mapBenefitActivityDashboardResponse(response.data, query).partners.flatMap(
      (partner) => partner.logs
    );
  }
  const response = await axiosRestInstance.get<BenefitActivityLogResponseType>(
    "/partner-program/benefit-activity-log/get",
    { params: { partner_ids: source.partnerId, page: 1, limit: ALL_LOGS_LIMIT } }
  );
  return response.data?.data || [];
};

/**
 * Loads filter dropdown options from all logs (ignoring current filters, search and paging)
 * once when `enabled`, and again each time `refresh` becomes true (e.g. the filter panel opens),
 * so options are ready before the panel renders and pick up newly added logs.
 */
const useBenefitLogFilterOptions = (
  source: BenefitLogFilterOptionsSource,
  enabled: boolean,
  refresh = false
): BenefitLogFilterOptions => {
  const [options, setOptions] = useState<BenefitLogFilterOptions>(
    EMPTY_BENEFIT_LOG_FILTER_OPTIONS
  );
  const partnerId = source.type === "partner" ? source.partnerId : undefined;
  const loadedKeyRef = useRef<string | null>(null);
  const latestRequestRef = useRef(0);

  useEffect(
    () => () => {
      latestRequestRef.current += 1;
    },
    []
  );

  useEffect(() => {
    if (!enabled) return;
    if (source.type === "partner" && !partnerId) return;
    const sourceKey = `${source.type}-${partnerId ?? ""}`;
    const isNewSource = loadedKeyRef.current !== sourceKey;
    if (!isNewSource && !refresh) return;
    loadedKeyRef.current = sourceKey;
    if (isNewSource) setOptions(EMPTY_BENEFIT_LOG_FILTER_OPTIONS);
    const requestId = ++latestRequestRef.current;
    fetchAllLogs(source)
      .then((logs) => {
        if (requestId !== latestRequestRef.current) return;
        setOptions(
          buildBenefitLogFilterOptions(logs, {
            showPartnerOnOwner: source.type === "dashboard",
          })
        );
      })
      .catch(() => {
        // Keep the previously loaded options if a refresh fails.
      });
    // `source` is an inline object; its identity is captured by type + partnerId.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, refresh, source.type, partnerId]);

  return options;
};

export default useBenefitLogFilterOptions;
